/**
 * Sidebar — primary navigation shell.
 *
 * Section-aware: the nav tree switches between the admin console and the user
 * portal based on the current URL prefix (/admin vs /portal). A single layout
 * shell serves both sections; only the nav config changes.
 *
 * Desktop: fixed left panel, 260 px expanded / 72 px collapsed.
 * Mobile:  off-canvas drawer, slides in from the left.
 *
 * State is owned by uiStore:
 *   sidebarCollapsed — desktop icon-only mode
 *   sidebarOpen      — mobile drawer visibility
 */

import { useState }               from 'react'
import { Link, useLocation }      from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  Calendar, CalendarDays, CalendarCheck, Plus,
  ClipboardList,
  BarChart2, PieChart, TrendingUp,
  Settings2, UserCog, ShieldCheck,
  Users, UserPlus,
  ChevronDown, ChevronLeft, ChevronRight,
  LogOut, Loader2, X,
  Bell, Mail, History,
  Building2, Palette, Globe2, Lock,
  KeyRound, Send,
} from 'lucide-react'
import { AppLogo } from '@components/brand'
import { UserAvatarCircle } from '@components/ui/UserAvatarCircle'

import { useUiStore, useAuthStore } from '@store'
import { useIsMobileNav }           from '@hooks'
import { useLogoutMutation }        from '@features/auth'
import { usePermission }            from '@features/roles'
import { ROUTES }                   from '@constants/routes'
import { BRANDING }                 from '@constants/branding'
import { cn }                       from '@utils'

// ── Navigation configs ──────────────────────────────────────────────────────────

function getNavGroups(pathname, role) {
  if (pathname.startsWith(ROUTES.PORTAL.ROOT)) return PORTAL_NAV_GROUPS
  if (role === 'super_admin') return SUPER_ADMIN_NAV_GROUPS
  return ADMIN_NAV_GROUPS
}

const SUPER_ADMIN_NAV_GROUPS = [
  {
    id: 'main', label: null,
    items: [
      { id: 'admin-dashboard', label: 'Dashboard', href: ROUTES.ADMIN.DASHBOARD, icon: LayoutDashboard, end: true },
    ],
  },
  {
    id: 'platform', label: 'Platform',
    items: [
      {
        id: 'companies', label: 'Companies', icon: Building2,
        match: '/admin/companies', defaultHref: ROUTES.ADMIN.COMPANIES.LIST,
        permission: 'companies:read',
        children: [
          { id: 'companies-list', label: 'All Companies', href: ROUTES.ADMIN.COMPANIES.LIST, icon: Building2, end: true },
          { id: 'companies-create', label: 'Create Company', href: ROUTES.ADMIN.COMPANIES.CREATE, icon: Plus, end: true, permission: 'companies:create' },
        ],
      },
    ],
  },
  {
    id: 'account', label: 'Account',
    items: [
      {
        id: 'settings', label: 'Settings', icon: Settings2,
        match: '/admin/settings', defaultHref: ROUTES.ADMIN.SETTINGS.PROFILE,
        children: [
          { id: 'settings-profile', label: 'Profile', href: ROUTES.ADMIN.SETTINGS.PROFILE, icon: UserCog, end: true },
          { id: 'settings-security', label: 'Security', href: ROUTES.ADMIN.SETTINGS.SECURITY, icon: Lock, end: true },
        ],
      },
    ],
  },
]

const ADMIN_NAV_GROUPS = [
  {
    id: 'main', label: null,
    items: [
      { id: 'admin-dashboard', label: 'Dashboard', href: ROUTES.ADMIN.DASHBOARD, icon: LayoutDashboard, end: true },
    ],
  },
  {
    id: 'management', label: 'Management',
    items: [
      {
        id: 'events', label: 'Events', icon: Calendar,
        match: '/admin/events', defaultHref: ROUTES.ADMIN.EVENTS.LIST,
        children: [
          { id: 'events-list',   label: 'All Events',   href: ROUTES.ADMIN.EVENTS.LIST,   icon: CalendarDays, end: true },
          { id: 'events-create', label: 'Create Event', href: ROUTES.ADMIN.EVENTS.CREATE, icon: Plus,         end: true },
        ],
      },
    ],
  },
  {
    id: 'administration', label: 'Administration',
    items: [
      {
        id: 'users', label: 'Users', icon: Users,
        match: '/admin/users', defaultHref: ROUTES.ADMIN.USERS.LIST,
        permission: 'users:read',
        children: [
          { id: 'users-list',   label: 'All Users', href: ROUTES.ADMIN.USERS.LIST,   icon: Users,    end: true },
          { id: 'users-create', label: 'Add User',  href: ROUTES.ADMIN.USERS.CREATE, icon: UserPlus, end: true, permission: 'users:create' },
        ],
      },
      {
        id: 'roles-nav', label: 'Roles & Permissions', icon: KeyRound,
        match: '/admin/roles', defaultHref: ROUTES.ROLES.LIST,
        permission: 'roles:read',
        children: [
          { id: 'roles-list',   label: 'All Roles',   href: ROUTES.ROLES.LIST,   icon: ShieldCheck, end: true },
          { id: 'roles-create', label: 'Create Role', href: ROUTES.ROLES.CREATE, icon: Plus,        end: true, permission: 'roles:create' },
        ],
      },
    ],
  },
  {
    id: 'analytics', label: 'Analytics',
    items: [
      {
        id: 'reports', label: 'Reports', icon: BarChart2,
        match: '/admin/reports', defaultHref: ROUTES.ADMIN.REPORTS.OVERVIEW,
        children: [
          { id: 'reports-overview',       label: 'Overview',              href: ROUTES.ADMIN.REPORTS.OVERVIEW,     icon: PieChart,      end: true },
          { id: 'reports-events',         label: 'Event Reports',         href: ROUTES.ADMIN.REPORTS.EVENTS,       icon: TrendingUp,    end: true },
          { id: 'reports-users',          label: 'User Reports',          href: ROUTES.ADMIN.REPORTS.USERS,        icon: Users,         end: true },
          { id: 'reports-assignments',    label: 'Assignment Reports',    href: ROUTES.ADMIN.REPORTS.ASSIGNMENTS,  icon: ClipboardList, end: true },
        ],
      },
    ],
  },
  {
    id: 'communication', label: 'Communication',
    items: [
      {
        id: 'communication-nav', label: 'Communication', icon: Mail,
        match: '/admin/communication', defaultHref: ROUTES.ADMIN.COMMUNICATION.TEMPLATES,
        children: [
          { id: 'email-templates',   label: 'Email Templates', href: ROUTES.ADMIN.COMMUNICATION.TEMPLATES,   icon: Mail, end: false },
          { id: 'comm-notif',       label: 'Notifications',   href: ROUTES.ADMIN.NOTIFICATIONS.LIST,        icon: Bell, end: false },
          { id: 'comm-sent-emails', label: 'Sent Emails',     href: ROUTES.ADMIN.EMAILS.SENT,               icon: Send, end: false },
        ],
      },
    ],
  },
  {
    id: 'account', label: 'Account',
    items: [
      {
        id: 'settings', label: 'Settings', icon: Settings2,
        match: '/admin/settings', defaultHref: ROUTES.ADMIN.SETTINGS.PROFILE,
        children: [
          { id: 'settings-profile',       label: 'Profile',        href: ROUTES.ADMIN.SETTINGS.PROFILE,       icon: UserCog,    end: false },
          { id: 'settings-account',       label: 'Account',        href: ROUTES.ADMIN.SETTINGS.ACCOUNT,       icon: ShieldCheck, end: false },
          { id: 'settings-organization',  label: 'Organization',   href: ROUTES.ADMIN.SETTINGS.ORGANIZATION,  icon: Building2,  end: false },
          { id: 'settings-branding',      label: 'Branding',       href: ROUTES.ADMIN.SETTINGS.BRANDING,      icon: Palette,    end: false },
          { id: 'settings-regional',      label: 'Regional',       href: ROUTES.ADMIN.SETTINGS.REGIONAL,      icon: Globe2,     end: false },
          { id: 'settings-notifications', label: 'Notifications',  href: ROUTES.ADMIN.SETTINGS.NOTIFICATIONS, icon: Bell,       end: false },
          { id: 'settings-security',      label: 'Security',       href: ROUTES.ADMIN.SETTINGS.SECURITY,      icon: Lock,       end: false },
        ],
      },
    ],
  },
]

const PORTAL_NAV_GROUPS = [
  {
    id: 'main', label: null,
    items: [
      { id: 'portal-dashboard', label: 'Dashboard', href: ROUTES.PORTAL.DASHBOARD, icon: LayoutDashboard, end: true },
    ],
  },
  {
    id: 'portal-events', label: 'My Events',
    items: [
      { id: 'portal-my-events', label: 'My Events', href: ROUTES.PORTAL.MY_EVENTS, icon: CalendarCheck, end: true },
    ],
  },
  {
    id: 'portal-notifications', label: 'Notifications',
    items: [
      { id: 'portal-notif', label: 'Notifications', href: ROUTES.PORTAL.NOTIFICATIONS, icon: Bell, end: true },
    ],
  },
  {
    id: 'portal-account', label: 'Account',
    items: [
      { id: 'portal-profile',       label: 'Profile',               href: ROUTES.PORTAL.PROFILE,                      icon: UserCog,   end: false },
      { id: 'portal-settings',      label: 'Settings',              href: ROUTES.PORTAL.SETTINGS,                     icon: Settings2, end: false },
      { id: 'portal-notif-prefs',   label: 'Notification Prefs',    href: ROUTES.PORTAL.SETTINGS_PAGES.NOTIFICATIONS, icon: Bell,      end: false },
    ],
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function isItemActive(pathname, href, end) {
  if (end) return pathname === href
  return pathname === href || pathname.startsWith(href + '/')
}

function isGroupActive(pathname, match) {
  return pathname.startsWith(match)
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Tooltip({ label }) {
  return (
    <span
      role="tooltip"
      className={cn(
        'absolute left-full ml-3 z-50 pointer-events-none',
        'px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap',
        'opacity-0 invisible group-hover:opacity-100 group-hover:visible',
        'transition-opacity duration-150',
      )}
      style={{ background: 'var(--ac-bg-surface)', color: 'var(--ac-text-base)', border: '1px solid var(--ac-border-base)', boxShadow: 'var(--ac-shadow-md)' }}
    >
      {label}
    </span>
  )
}

function ActiveBar() {
  return <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full" style={{ background: 'var(--ac-secondary)' }} />
}

function NavLeaf({ item, collapsed, onNavigate }) {
  const { pathname } = useLocation()
  const active = isItemActive(pathname, item.href, item.end)

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex items-center gap-3 h-9 rounded-xl text-sm font-medium',
        'transition-colors duration-150 outline-none',
        'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
        collapsed ? 'justify-center px-0' : 'px-3',
        active
          ? 'text-[var(--ac-secondary)] bg-[var(--ac-secondary)]/10'
          : 'text-[var(--ac-text-muted)] hover:bg-[var(--ac-bg-subtle)] hover:text-[var(--ac-text-base)]',
      )}
    >
      {active && <ActiveBar />}
      <item.icon size={collapsed ? 17 : 15} strokeWidth={active ? 2 : 1.75} className="flex-shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {collapsed  && <Tooltip label={item.label} />}
    </Link>
  )
}

function NavBranch({ item, isOpen, onToggle, collapsed, onNavigate }) {
  const { pathname } = useLocation()
  const active = isGroupActive(pathname, item.match)

  if (collapsed) {
    return (
      <Link
        to={item.defaultHref}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'group relative flex items-center justify-center h-9 rounded-xl',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
          active ? 'text-[var(--ac-secondary)] bg-[var(--ac-secondary)]/10' : 'text-[var(--ac-text-muted)] hover:bg-[var(--ac-bg-subtle)] hover:text-[var(--ac-text-base)]',
        )}
      >
        {active && <ActiveBar />}
        <item.icon size={17} strokeWidth={active ? 2 : 1.75} />
        <Tooltip label={item.label} />
      </Link>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex items-center gap-3 w-full px-3 h-9 rounded-xl text-sm font-medium text-left',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)] focus-visible:ring-inset',
          active ? 'text-[var(--ac-text-base)]' : 'text-[var(--ac-text-muted)] hover:bg-[var(--ac-bg-subtle)] hover:text-[var(--ac-text-base)]',
        )}
      >
        <item.icon size={17} strokeWidth={active ? 2 : 1.75} className="flex-shrink-0" />
        <span className="flex-1 truncate">{item.label}</span>
        <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2, ease: 'easeInOut' }} className="flex items-center" style={{ color: 'var(--ac-text-subtle)' }}>
          <ChevronDown size={13} strokeWidth={2.5} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div key="children" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: 'easeInOut' }} className="overflow-hidden">
            <div className="ml-5 mt-0.5 pl-3 space-y-0.5 pb-0.5" style={{ borderLeft: '1px solid var(--ac-border-base)' }}>
              {item.children.map((child) => (
                <NavLeaf key={child.id} item={child} collapsed={false} onNavigate={onNavigate} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function SidebarNav({ groups, collapsed, openGroups, onToggleGroup, onNavigate }) {
  const { can } = usePermission()

  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-0.5 scrollbar-theme">
      {groups.map((group) => {
        // Filter items by permission
        const visibleItems = group.items.filter(
          (item) => !item.permission || can(item.permission),
        )
        if (visibleItems.length === 0) return null

        return (
          <div key={group.id}>
            {group.label && (
              collapsed
                ? <div className="my-2 mx-2 h-px" style={{ background: 'var(--ac-border-base)' }} />
                : <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider select-none" style={{ color: 'var(--ac-text-subtle)', letterSpacing: '0.08em' }}>{group.label}</p>
            )}
            <div className="space-y-0.5">
              {visibleItems.map((item) => {
                // Filter children by permission
                const itemWithFilteredChildren = item.children
                  ? { ...item, children: item.children.filter((c) => !c.permission || can(c.permission)) }
                  : item

                return item.children
                  ? <NavBranch key={item.id} item={itemWithFilteredChildren} isOpen={openGroups.has(item.id)} onToggle={() => onToggleGroup(item.id)} collapsed={collapsed} onNavigate={onNavigate} />
                  : <NavLeaf   key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              })}
            </div>
          </div>
        )
      })}
    </nav>
  )
}

function SidebarLogo({ collapsed, onClose }) {
  const { toggleCollapsed } = useUiStore((s) => ({ toggleCollapsed: s.toggleCollapsed }))

  return (
    <div className="flex items-center h-16 px-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--ac-border-base)' }}>
      {/* Logo — wordmark when expanded, icon when collapsed */}
      {collapsed
        ? (
          <AppLogo
            variant="icon"
            height={38}
            style={{ borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}
          />
        )
        : (
          <AppLogo
            variant="wordmark"
            height={48}
            style={{ flexShrink: 0 }}
          />
        )
      }

      <div className="flex-1" />

      {/* Desktop: collapse / expand toggle */}
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={cn('hidden lg:flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 hover:bg-[var(--ac-bg-subtle)]')}
        style={{ color: 'var(--ac-text-subtle)' }}
      >
        {collapsed ? <ChevronRight size={14} strokeWidth={2} /> : <ChevronLeft size={14} strokeWidth={2} />}
      </button>

      {/* Mobile: close drawer */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close navigation"
        className={cn('flex lg:hidden items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 hover:bg-[var(--ac-bg-subtle)]')}
        style={{ color: 'var(--ac-text-subtle)' }}
      >
        <X size={16} strokeWidth={2} />
      </button>
    </div>
  )
}

function SidebarUser({ collapsed }) {
  const user                          = useAuthStore((s) => s.user)
  const { mutate: logout, isPending } = useLogoutMutation()

  return (
    <div className="flex-shrink-0 flex items-center gap-3 px-3 py-3" style={{ borderTop: '1px solid var(--ac-border-base)' }}>
      <UserAvatarCircle user={user} size={32} />
      {!collapsed && (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate leading-tight" style={{ color: 'var(--ac-text-base)' }}>{user?.name ?? 'User'}</p>
            {user?.email && <p className="text-xs truncate leading-tight mt-0.5" style={{ color: 'var(--ac-text-muted)' }}>{user.email}</p>}
          </div>
          <button type="button" onClick={() => logout()} disabled={isPending} aria-label="Sign out"
            className={cn('flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 disabled:opacity-50 hover:bg-[var(--ac-bg-subtle)]')} style={{ color: 'var(--ac-text-subtle)' }}>
            {isPending ? <Loader2 size={15} className="animate-spin" /> : <LogOut size={15} strokeWidth={1.75} />}
          </button>
        </>
      )}
    </div>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

export function Sidebar() {
  const { pathname } = useLocation()
  const user           = useAuthStore((s) => s.user)
  const groups         = getNavGroups(pathname, user?.role)
  const isMobileNav  = useIsMobileNav()

  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed)
  const sidebarOpen      = useUiStore((s) => s.sidebarOpen)
  const setSidebarOpen   = useUiStore((s) => s.setSidebarOpen)

  // Open the group that contains the active route on first render.
  const [openGroups, setOpenGroups] = useState(() => {
    const open = new Set()
    groups.forEach(({ items }) => {
      items.forEach((item) => {
        if (item.children?.some((c) => isItemActive(pathname, c.href, c.end))) open.add(item.id)
      })
    })
    return open
  })

  function toggleGroup(id) {
    setOpenGroups((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function handleNavigate() {
    setSidebarOpen(false)
  }

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 flex flex-col flex-shrink-0',
        sidebarCollapsed ? 'w-[72px]' : 'w-[260px]',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0',
        isMobileNav && !sidebarOpen && 'pointer-events-none',
        'z-[300] lg:z-[100]',
        'transition-[width,transform] duration-250 ease-in-out',
      )}
      style={{ background: 'var(--ac-bg-surface)', borderRight: '1px solid var(--ac-border-base)' }}
      aria-hidden={isMobileNav && !sidebarOpen ? true : undefined}
    >
      <SidebarLogo collapsed={sidebarCollapsed} onClose={() => setSidebarOpen(false)} />
      <SidebarNav groups={groups} collapsed={sidebarCollapsed} openGroups={openGroups} onToggleGroup={toggleGroup} onNavigate={handleNavigate} />
      <SidebarUser collapsed={sidebarCollapsed} />
    </aside>
  )
}
